import React, { useEffect, useState } from 'react';
import { Moon, Sparkles, ChevronRight, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

/**
 * CycleCard — opt-in Women's Wellness layer.
 *
 * Two states:
 *   1. Not opted in → setup form (last period, cycle length, age band)
 *   2. Opted in → shows current phase + phase-specific guidance for
 *      movement, nutrition, mental.
 *
 * The user can disable tracking at any time. Phase guidance feeds Nikki
 * via the chat_llm context pack — she'll subtly adjust suggestions.
 */

const PHASE_COLORS = {
  menstrual:  '#f87171',
  follicular: '#22d3ee',
  ovulation:  '#CCFF00',
  luteal:     '#a855f7',
};

const AGE_BANDS = [
  { v: 'under_30', label: 'under 30' },
  { v: '30s', label: '30s' },
  { v: '40s', label: '40s' },
  { v: '50_plus', label: '50+' },
];

const SetupForm = ({ onSaved, onCancel, initial }) => {
  const [lastPeriod, setLastPeriod] = useState(initial?.last_period_start || '');
  const [cycleDays, setCycleDays] = useState(initial?.avg_cycle_days || 28);
  const [ageBand, setAgeBand] = useState(initial?.age_band || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!lastPeriod) { toast.error('Pick the first day of your last period.'); return; }
    setSaving(true);
    try {
      await api.setupCycleTracking({
        tracking_enabled: true,
        last_period_start: lastPeriod,
        avg_cycle_days: Number(cycleDays) || 28,
        age_band: ageBand || null,
      });
      toast.success('Cycle tracking on — Nikki will adjust gently around your phase.');
      onSaved?.();
    } catch {
      toast.error('Could not save. Try again?');
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="cycle-setup-form">
      <p className="text-xs text-zinc-400 leading-relaxed">
        Opt in to share your cycle with Nikki. She'll soften workouts in your luteal phase,
        nudge mobility during menstruation, and match your follicular energy. Tracked privately,
        always reversible.
      </p>
      <div>
        <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">First day of last period</label>
        <input
          type="date"
          value={lastPeriod}
          onChange={(e) => setLastPeriod(e.target.value)}
          max={new Date().toISOString().slice(0,10)}
          className="w-full bg-zinc-900 border border-zinc-700 rounded-sm px-3 py-2 text-sm text-white focus:border-primary outline-none"
          data-testid="cycle-last-period"
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">Avg cycle length (days)</label>
          <input
            type="number"
            min="20" max="45"
            value={cycleDays}
            onChange={(e) => setCycleDays(e.target.value)}
            className="w-full bg-zinc-900 border border-zinc-700 rounded-sm px-3 py-2 text-sm text-white focus:border-primary outline-none"
            data-testid="cycle-avg-days"
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider text-zinc-500 block mb-1">Age band (optional)</label>
          <select
            value={ageBand}
            onChange={(e) => setAgeBand(e.target.value)}
            className="w-full bg-zinc-900 border border-zinc-700 rounded-sm px-3 py-2 text-sm text-white focus:border-primary outline-none"
            data-testid="cycle-age-band"
          >
            <option value="">—</option>
            {AGE_BANDS.map(a => <option key={a.v} value={a.v}>{a.label}</option>)}
          </select>
        </div>
      </div>
      <div className="flex gap-2">
        <button
          onClick={save}
          disabled={saving}
          className="flex-1 px-4 py-2 bg-primary text-black font-secondary font-bold text-sm uppercase tracking-wider rounded-sm hover:bg-primary/90 disabled:opacity-40"
          data-testid="cycle-save"
        >
          {saving ? 'saving…' : 'turn on'}
        </button>
        {onCancel && (
          <button
            onClick={onCancel}
            className="px-4 py-2 border border-zinc-700 text-zinc-400 hover:text-white font-secondary text-sm uppercase tracking-wider rounded-sm"
            data-testid="cycle-cancel"
          >
            cancel
          </button>
        )}
      </div>
    </div>
  );
};

const PhaseView = ({ data, onEdit, onDisable }) => {
  const { current, profile } = data;
  if (!current) return null;
  const color = PHASE_COLORS[current.phase] || '#a855f7';
  const guidance = current.guidance || {};

  return (
    <div className="space-y-4" data-testid="cycle-phase-view">
      <div className="flex items-center gap-4">
        <div
          className="w-16 h-16 rounded-full flex flex-col items-center justify-center border-2"
          style={{ borderColor: color, background: `${color}15` }}
        >
          <span className="font-secondary font-bold text-xl" style={{ color }}>{current.day_of_cycle}</span>
          <span className="text-[8px] uppercase tracking-wider text-zinc-400">day</span>
        </div>
        <div className="flex-1">
          <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-secondary">CURRENT PHASE</p>
          <h3 className="font-secondary font-bold text-xl" style={{ color }} data-testid="cycle-phase-label">{current.phase_label}</h3>
          <p className="text-[11px] text-zinc-500">day {current.day_of_cycle} of ~{current.cycle_length}</p>
        </div>
      </div>

      {guidance.movement && (
        <div className="border-l-2 pl-3 py-1" style={{ borderColor: color }}>
          <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-secondary mb-0.5">MOVEMENT</p>
          <p className="text-xs text-zinc-300 leading-relaxed">{guidance.movement}</p>
        </div>
      )}
      {guidance.nutrition && (
        <div className="border-l-2 pl-3 py-1" style={{ borderColor: color }}>
          <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-secondary mb-0.5">NUTRITION</p>
          <p className="text-xs text-zinc-300 leading-relaxed">{guidance.nutrition}</p>
        </div>
      )}
      {guidance.mental && (
        <div className="border-l-2 pl-3 py-1" style={{ borderColor: color }}>
          <p className="text-[10px] uppercase tracking-wider text-zinc-500 font-secondary mb-0.5">MENTAL</p>
          <p className="text-xs text-zinc-300 leading-relaxed">{guidance.mental}</p>
        </div>
      )}

      <div className="flex items-center justify-between pt-2 border-t border-zinc-800">
        <button
          onClick={onEdit}
          className="text-[11px] text-zinc-400 hover:text-primary flex items-center gap-1"
          data-testid="cycle-edit"
        >
          edit cycle <ChevronRight className="w-3 h-3" />
        </button>
        <button
          onClick={onDisable}
          className="text-[11px] text-zinc-500 hover:text-red-400 flex items-center gap-1"
          data-testid="cycle-disable"
        >
          <X className="w-3 h-3" /> turn off
        </button>
      </div>
    </div>
  );
};

const CycleCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const r = await api.getCycleToday();
      setData(r.data);
    } catch {
      setData(null);
    } finally { setLoading(false); }
  };

  useEffect(() => { reload(); }, []);

  const handleSaved = async () => { setEditing(false); await reload(); };

  const handleDisable = async () => {
    if (!window.confirm('Turn off cycle tracking? You can opt back in anytime.')) return;
    try {
      await api.setupCycleTracking({ tracking_enabled: false });
      toast.success('Cycle tracking turned off.');
      await reload();
    } catch { toast.error('Could not turn off. Try again?'); }
  };

  if (loading) return null;

  const showSetup = editing || !data?.tracking_enabled || !data?.current;

  return (
    <div className="hpme-card p-5" data-testid="cycle-card">
      <div className="hpme-card-corner" />
      <div className="flex items-center gap-2 mb-4">
        {data?.tracking_enabled ? <Moon className="w-4 h-4 text-accent-purple" /> : <Sparkles className="w-4 h-4 text-accent-purple" />}
        <span className="text-[10px] uppercase tracking-[0.2em] text-accent-purple font-secondary font-bold">
          {data?.tracking_enabled ? "WOMEN'S WELLNESS" : "CYCLE-AWARE WELLNESS (OPT-IN)"}
        </span>
      </div>
      {showSetup ? (
        <SetupForm
          onSaved={handleSaved}
          onCancel={data?.tracking_enabled ? () => setEditing(false) : null}
          initial={data?.profile}
        />
      ) : (
        <PhaseView data={data} onEdit={() => setEditing(true)} onDisable={handleDisable} />
      )}
    </div>
  );
};

export default CycleCard;
